import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import { map } from 'lit/directives/map.js';
import { ref, createRef } from 'lit/directives/ref.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { getMessagePlural } from '@/localization.js';
import messages from '../localization.json';
import { EVENTS } from '../constants';
import styles from '../style/select.scss?inline';
import { ATTRIBUTES, DEFAULT_INPUT_PROPERTIES } from '@/components/input/constants.js';
import { FormValidationController } from '@/controllers/FormValidationController.js';
import debounce from 'lodash/debounce';
import { checkValidity } from '@/components/input/common.js';

export default class SotbitSelect extends LitElement {
    static formAssociated = true;

    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        ...DEFAULT_INPUT_PROPERTIES,
        _displayValue: { type: String, state: true },
        size: { type: String },
        multiple: { type: Boolean, reflect: true },
        open: { type: Boolean, state: true },
        defaultIcon: { type: String, attribute: 'default-icon' },
        showLeftIcon: { type: Boolean, attribute: 'show-left-icon' },
        showRightIcon: { type: Boolean, attribute: 'show-right-icon' },
        hasLeftIcons: { type: Boolean, state: true },
        hasRightIcons: { type: Boolean, state: true },
        clearable: { type: Boolean },
    };

    constructor() {
        super();

        this.label = null;
        this.placeholder = null;
        this.multiple = false;
        this.open = false;
        this.options = [];
        this.defaultIcon = '';
        this.form = null;
        this.showLeftIcon = false;
        this.showRightIcon = false;

        this._internals = this.attachInternals();
        this._modalRef = null;
        this._selectedOptions = [];
        this._defaultOption = null;
        this._displayValue = this.placeholder;
        this._inputStyleRef = createRef();
        this._optionsSlotRef = createRef();
        this._displayValueInputRef = createRef();
        this.inputRef = createRef();
        this._movedSlots = false;
        this._defaultValue = null;
        this._isArray = false;

        this._resizeObserver = new ResizeObserver(this._handleResizeObserve);
        this._validationController = new FormValidationController(this, {
            getValue: () => this.value,
            getInputRef: () => this.inputRef,
        });
    }

    get type() {
        return this.multiple ? 'select-multiple' : 'select-one';
    }

    get value() {
        if (this.multiple) {
            if (this._isArray) {
                return this.selectedOptions.map((item) => item.value);
            }

            return this.selectedOptions.map((item) => item.value).join(', ');
        }

        return this.selectedOptions?.[0]?.value ?? '';
    }

    /**
     * @param {string | string[]} newValue
     */
    set value(newValue) {
        if (!Array.isArray(newValue)) {
            newValue = String(newValue ?? '');
        }

        this._syncOptionsFromValue(newValue);
        this._updateDisplayValue();

        if (this.error) {
            this.removeAttribute(ATTRIBUTES.ERROR);
            this.removeAttribute(ATTRIBUTES.ERROR_TEXT);
        }
    }

    get selectedOptions() {
        return (this._selectedOptions ??= this.options?.filter((option) => option.selected));
    }

    get options() {
        if (this._options?.length) {
            return this._options;
        }

        if (!this._optionsSlotRef?.value) {
            return this._options;
        }

        return (this.options = this.getOptionsFromSlot());
    }

    set options(options) {
        this._options = Array.from(options);
    }

    get selectedOption() {
        if (this.multiple) {
            return this.selectedOptions;
        }

        return this.selectedOptions[0] ?? null;
    }

    get selectedIndex() {
        return this.options.findIndex((el) => el.selected);
    }

    focus(options) {
        this.inputRef?.value?.focus(options);
    }

    getOptionsFromSlot() {
        const assignedNodes = this._optionsSlotRef.value.assignedNodes({
            flatten: true,
        });
        return assignedNodes.filter((node) => node.nodeName === 'SOTBIT-SELECT-OPTION');
    }

    getFormValue() {
        const formData = new FormData();

        if (this._isArray) {
            this.value?.forEach((value) => {
                formData.append(this.name, value);
            });
        } else {
            formData.append(this.name, this.value);
        }

        return formData;
    }

    connectedCallback() {
        super.connectedCallback();

        this.name = this.name || null;
        this.value = this.value || null;
        this._defaultValue = this.value;
        this.disabled = this.disabled || false;
        this.multiple = this.multiple || false;
    }

    disconnectedCallback() {
        this._movedSlots = false;
        super.disconnectedCallback();
        document.removeEventListener(EVENTS.CLICK, this._handleDocumentClick);
    }

    formAssociatedCallback(form) {
        this._validationController.removeInputHandlers();
        this._internals?.setFormValue(this.getFormValue());
        this.form = form;
        this._validationController?.validate();
        this._validationController.setInputHandlers();
    }

    formDisabledCallback(disabled) {
        this.toggleAttribute('disabled', disabled);
    }

    formResetCallback() {
        this.value = this._defaultValue;
    }

    formStateRestoreCallback(state) {
        this.value = state;
    }

    checkValidity(reportValidity) {
        return checkValidity(this, reportValidity);
    }

    willUpdate(changedProperties) {
        if (changedProperties.has('open') && typeof changedProperties.get('open') !== 'undefined') {
            if (this.open) {
                this.show();
            } else {
                this.hide();
            }
        }

        if (changedProperties.has('name')) {
            this._isArray = this.name?.endsWith('[]');
        }
    }

    _handleResizeObserve = () => {
        if (this.open) {
            this.hide();
        }
    };

    _getModal() {
        const modal = document.createElement('sotbit-dropdown-content');

        modal.setDropdown(this);
        modal.toggleAttribute('max-height', this.maxHeight);
        modal.setAttribute('use-teleport', '');
        modal.setAttribute('variant', 'select');

        const inputContainer = this._inputStyleRef?.value?.getInputContainer();
        if (inputContainer) {
            modal?.setRelativeElement(inputContainer);
        }

        return modal;
    }

    show() {
        this._modal = this._getModal();

        document.addEventListener('click', this._handleDocumentClick);

        if (this._modal === null) {
            return;
        }

        this._movedSlots = true;
        this.options?.forEach((option) => {
            this._modal.appendChild(option);
        });

        this._modal.addEventListener(
            'close',
            () => {
                this.removeAttribute('open');
            },
            { once: true }
        );

        this._modal.addEventListener(
            'remove',
            () => {
                this.removeAttribute('open');
            },
            { once: true }
        );

        document.body.appendChild(this._modal);
        window.addEventListener('resize', this._handleResizeObserve);
    }

    hide() {
        this._movedSlots = true;
        this.open = false;

        if (this._modal !== null) {
            this._modal?.remove();
            this._modal = null;

            this.options?.forEach((option) => {
                this.appendChild(option);
            });
        }

        document.removeEventListener('click', this._handleDocumentClick);
        window.removeEventListener('resize', this._handleResizeObserve);
    }

    updated(changedProperties) {
        super.updated(changedProperties);

        if (this.multiple && changedProperties.has('_displayValue')) {
            const fullText = this._selectedOptions.map((item) => item.innerText.trim()).join(', ');

            if (this._displayValue === fullText && this._selectedOptions.length > 0) {
                this._checkOverflow();
            }
        }

        if (changedProperties.has('_displayValue')) {
            this._inputStyleRef.value?.calculatePlaceholderShow();
            this._inputStyleRef.value?.calculateSmallLabel();
        }
    }

    /**
     * @param {string[] | string} value
     * @private
     */
    _syncOptionsFromValue(value) {
        if (!this.options || this.options.length === 0) {
            return;
        }

        if (value && !Array.isArray(value)) {
            value = this.multiple ? value.split(', ').filter((v) => v) : [value];
        }

        if (value.length) {
            this.options.forEach((opt) => opt.removeAttribute('selected'));

            this._selectedOptions = this.options.filter((opt) => {
                const shouldBeSelected = value.includes(opt.value);

                if (shouldBeSelected) {
                    opt.setAttribute('selected', '');
                }

                return shouldBeSelected;
            });
        } else {
            this._selectedOptions = this.options.filter((opt) => opt.selected);

            if (this._selectedOptions?.length < 1 && !this.multiple && !this.clearable) {
                if (this._defaultOption === null) {
                    this._defaultOption = this.options[0];
                }

                this._defaultOption?.setAttribute('selected', '');

                this._selectedOptions = [this._defaultOption];
            }

            if (this._selectedOptions?.length > 1 && !this.multiple) {
                for (let i = 0; i < this._selectedOptions.length - 1; i++) {
                    this._selectedOptions[i]?.removeAttribute('selected', '');
                }

                this._selectedOptions = this.options.filter((opt) => opt.selected);
            }
        }

        this._internals.setFormValue(this.getFormValue());
        this._updateDisplayValue();
    }

    _checkOverflow() {
        const input = this._displayValueInputRef.value;

        if (!input) {
            return;
        }

        requestAnimationFrame(() => {
            if (input.scrollWidth > input.clientWidth) {
                this._displayValue = getMessagePlural(messages, 'selectedOptions', this._selectedOptions.length, {
                    '#COUNT#': this._selectedOptions.length,
                });
            }
        });
    }

    _updateDisplayValue() {
        const oldDisplayValue = this._displayValue;
        if (this.multiple) {
            if (this._selectedOptions.length > 0) {
                this._displayValue = this._selectedOptions.map((item) => item.innerText.trim()).join(', ');
            } else {
                this._displayValue = this.placeholder;
            }
        } else {
            this._displayValue = this._selectedOptions[0]?.innerText.trim() || this.placeholder;
        }

        this.hasLeftIcons = this.getSelectedOptionLeftIcons()?.length > 0;
        this.hasRightIcons = this.getSelectedOptionRightIcons()?.length > 0;
        this._inputStyleRef?.value?.calculateSmallLabel();

        this.requestUpdate('_displayValue', oldDisplayValue);
    }

    handleSlotChange() {
        this._slotChangeDebounce();
    }

    _slotChangeDebounce = debounce(() => {
        if (this._movedSlots) {
            this._movedSlots = false;
            return;
        }

        this.options = this.getOptionsFromSlot();

        this._syncOptionsFromValue(this.getAttribute('value'));
        for (const option of this.options) {
            option.toggleAttribute('multiple', this.multiple);
            option.removeEventListener('click', this._handleSelectOption);
            option.addEventListener('click', this._handleSelectOption);
        }

        this.dispatchEvent(
            new Event(EVENTS.OPTIONS_CHANGE, {
                bubbles: true,
                composed: true,
            })
        );
    }, 60);

    _handleDocumentClick = (event) => {
        if (this.open && !event.composedPath().includes(this)) {
            this.open = this.multiple && event.composedPath().includes(this._modal);
        }
    };

    toggleDropdown() {
        if (this.disabled) {
            return;
        }

        this.open = !this.open;
    }

    resetClickHandler(e) {
        e.preventDefault();
        e.stopPropagation();
        this.selectOption(null, true);
        this._internals.setFormValue(this.getFormValue());
        this._updateDisplayValue();
    }

    selectOption(option, dispatchEvent = false) {
        if (this.disabled || option?.hasAttribute('disabled')) {
            return;
        }

        if (this.multiple && option?.value) {
            const index = this._selectedOptions.findIndex((item) => item.value === option?.value);

            if (index >= 0) {
                this._selectedOptions.splice(index, 1);
                option?.removeAttribute('selected');
            } else {
                this._selectedOptions.push(option);
                option?.setAttribute('selected', '');
            }

            this._selectedOptions = [...this._selectedOptions];
        } else {
            this.options.forEach((option) => option.removeAttribute('selected'));
            option?.setAttribute('selected', '');
            this._selectedOptions = option ? [option] : [];
        }

        if (this._isArray) {
            this.value = this._selectedOptions.map((item) => item.value);
        } else {
            this.value = this._selectedOptions.map((item) => item.value).join(', ');
        }

        if (dispatchEvent) {
            this.dispatchEvent(
                new CustomEvent(EVENTS.CHANGE, {
                    detail: {
                        value: this.value,
                    },
                    bubbles: true,
                    composed: true,
                })
            );
        }
    }

    _handleSelectOption = ({ currentTarget }) => {
        this.selectOption(currentTarget, true);
    };

    getSelectedOptionLeftIcons() {
        return !this.multiple ? this.selectedOption?.getLeftIcons() : [];
    }

    getSelectedOptionRightIcons() {
        return !this.multiple ? this.selectedOption?.getRightIcons() : [];
    }

    render() {
        const cloneAndConvertToHtml = (node) => {
            const cloneNode = node.cloneNode(true);
            return html`${unsafeHTML(cloneNode.outerHTML)}`;
        };

        return html`
            <input
                class="hidden-input"
                type="text"
                ${ref(this.inputRef)}
                .value="${this.value?.toString()}"
                ?required="${this.required}"
                ?disabled="${this.disabled}"
                ?readonly="${this.readonly}"
            />
            <sotbit-input-style
                size="${this.size || nothing}"
                label="${this.label || nothing}"
                @click=${this.toggleDropdown}
                default-slot
                default-slot-position="top"
                ${ref(this._inputStyleRef)}
                class="input"
                ?error="${this.error}"
                error-text="${this.errorText || nothing}"
                helper-text="${this.helperText || nothing}"
                ?clearable="${this.clearable && this.selectedOptions.length}"
                no-readonly-style
                exportparts="clear-button-wrapper, clear-button"
            >
                <input
                    slot="input"
                    type="text"
                    readonly
                    value="${this._displayValue}"
                    ${ref(this._displayValueInputRef)}
                    ?required="${this.required}"
                    ?disabled="${this.disabled}"
                />
                ${this.showLeftIcon
                    ? this.hasLeftIcons
                        ? map(this.getSelectedOptionLeftIcons(), cloneAndConvertToHtml)
                        : html`<sotbit-icon size="l" no-padding icon="select" slot="icon-left"></sotbit-icon>`
                    : ''}
                ${this.showRightIcon
                    ? this.hasRightIcons > 0
                        ? map(this.getSelectedOptionRightIcons(), cloneAndConvertToHtml)
                        : html`<sotbit-icon size="l" no-padding icon="select" slot="icon-right"></sotbit-icon>`
                    : ''}

                <sotbit-icon slot="icon-right" size="m" icon="dropdown"></sotbit-icon>
                <div hidden>
                    <slot ${ref(this._optionsSlotRef)} @slotchange=${this.handleSlotChange}></slot>
                </div>
                <slot name="clear-button" slot="clear-button" @click="${this.resetClickHandler}">
                    <sotbit-icon icon="close" size="m" variant="secondary" pointer></sotbit-icon>
                </slot>
            </sotbit-input-style>
        `;
    }
}
